/**
 * @file inward-efd-cell.tsx
 * @module app/modules/inward/components/inward-efd-cell
 * @description EFD cell of the Inward report for users with "Verify EFD receipt" (Inward right
 *   76): a tick box that marks the GRN's EFD / TRA receipt as checked by a person (or removes the
 *   mark), saved at once, with who and when. Others see the status text only.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import APICallService from "../../../../api/apiCallService";
import { INWARD } from "../../../../api/apiEndPoints";
import { success } from "../../../../Global/toast";
import { PermissionModule } from "../../../../utils/constants";
import { InwardReportRow } from "../utils/inward-report";

interface InwardEfdCellProps {
  row: InwardReportRow;
  warehouseId?: string;
  /** called with the saved state so the report (and its PNG) updates in place */
  onSaved: (state: {
    efdVerified: boolean;
    efdVerifiedBy: string;
    efdVerifiedAt: string;
  }) => void;
}

const eatDateTime = (iso: string | null | undefined) => {
  if (!iso) return "";
  const d = new Date(new Date(iso).getTime() + 3 * 60 * 60 * 1000);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getUTCDate())}/${p(
    d.getUTCMonth() + 1
  )}/${d.getUTCFullYear()} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
};

const InwardEfdCell = ({ row, warehouseId, onSaved }: InwardEfdCellProps) => {
  const [saving, setSaving] = useState(false);
  const verified = row.efdVerified === true;

  const toggle = async () => {
    if (saving || !row.grnId) return;
    setSaving(true);
    const res = await new APICallService(
      INWARD.setEfdVerification,
      { verified: !verified },
      { id: row.grnId, a: "efd-verification" },
      "",
      "",
      "",
      PermissionModule.Inward,
      warehouseId
    ).callAPI();
    setSaving(false);
    if (!res || typeof res.efdVerified !== "boolean") return;
    onSaved({
      efdVerified: res.efdVerified,
      efdVerifiedBy: res.efdVerifiedBy || "",
      efdVerifiedAt: eatDateTime(res.efdVerifiedAt),
    });
    success(
      res.efdVerified
        ? `EFD verified for ${row.grnRefKey}`
        : `EFD verification removed for ${row.grnRefKey}`
    );
  };

  return (
    <div
      className="d-flex flex-column align-items-center gap-1"
      data-testid={`efd-cell-${row.grnRefKey}`}
    >
      <div className="d-flex align-items-center gap-2">
        <Form.Check
          type="checkbox"
          id={`efd-${row.grnRefKey}`}
          checked={verified}
          disabled={saving || !row.grnId}
          onChange={toggle}
          label={verified ? "Verified" : "Not verified"}
          className="fw-600 mb-0"
          data-testid={`efd-check-${row.grnRefKey}`}
        />
        {saving && <Spinner animation="border" size="sm" />}
      </div>
      {verified ? (
        <span
          className="fs-12 text-muted"
          data-testid={`efd-by-${row.grnRefKey}`}
        >
          {[row.efdVerifiedBy, row.efdVerifiedAt].filter(Boolean).join(" · ")}
        </span>
      ) : (
        row.efd === "Pending (code entered)" && (
          <span className="fs-12 text-muted">Receipt code entered</span>
        )
      )}
    </div>
  );
};

export default InwardEfdCell;
